import { useState, useMemo, useCallback } from "react";
import { addMinutes } from "date-fns";
import type { Tables } from "@/integrations/supabase/types";

type CourtRow = Tables<"courts">;

interface BookingDayRow extends Tables<"bookings"> {
  courts: { name: string; hall_id: string } | null;
}

interface UseBookingStateParams {
  bookings: BookingDayRow[];
  courts: CourtRow[];
  hallHours: { start: number; end: number } | null;
  selectedDate: Date;
  minDuration: number;
  maxDuration: number;
}

export function useBookingState(params: UseBookingStateParams) {
  const { bookings, courts, hallHours, selectedDate, minDuration, maxDuration } = params;

  const [selectedSlots, setSelectedSlots] = useState<Array<{ courtId: string; startTime: Date }>>([]);

  // Backwards compat: derive confirmSlot from first selectedSlot
  const confirmSlot = selectedSlots.length > 0 ? selectedSlots[0] : null;

  const setConfirmSlot = useCallback((slot: { courtId: string; startTime: Date } | null) => {
    if (slot) {
      setSelectedSlots([slot]);
    } else {
      setSelectedSlots([]);
    }
  }, []);

  const handleSlotClick = useCallback((courtId: string, startTime: Date, isShift?: boolean) => {
    setSelectedSlots((prev) => {
      if (isShift && prev.length > 0 && prev[0].startTime.getTime() === startTime.getTime()) {
        // Add court at same time (shift+click)
        if (!prev.find((s) => s.courtId === courtId)) {
          return [...prev, { courtId, startTime: prev[0].startTime }];
        }
        return prev;
      }
      return [{ courtId, startTime }];
    });
  }, []);

  const handleAddCourt = useCallback((cId: string) => {
    setSelectedSlots((prev) => {
      if (prev.length === 0) return prev;
      if (prev.find((s) => s.courtId === cId)) return prev;
      return [...prev, { courtId: cId, startTime: prev[0].startTime }];
    });
  }, []);

  const handleRemoveCourt = useCallback((cId: string) => {
    setSelectedSlots((prev) => prev.filter((s) => s.courtId !== cId));
  }, []);

  // Calculate max available minutes from a given slot
  const maxAvailableMinutes = useMemo(() => {
    if (!confirmSlot || !hallHours) return maxDuration;
    const { courtId, startTime } = confirmSlot;
    const dayEnd = new Date(selectedDate);
    dayEnd.setHours(hallHours.end, 0, 0, 0);

    let maxMin = Math.floor((dayEnd.getTime() - startTime.getTime()) / 60_000);

    // Check for next booking on this court
    const courtBookings = bookings
      .filter((b: BookingDayRow) => b.court_id === courtId && b.status !== "cancelled")
      .map((b: BookingDayRow) => new Date(b.start_time))
      .filter((bStart: Date) => bStart > startTime)
      .sort((a: Date, b: Date) => a.getTime() - b.getTime());

    if (courtBookings.length > 0) {
      const nextBooking = courtBookings[0] as Date;
      maxMin = Math.min(maxMin, Math.floor((nextBooking.getTime() - startTime.getTime()) / 60_000));
    }

    return Math.min(maxMin, maxDuration);
  }, [confirmSlot, hallHours, bookings, maxDuration, selectedDate]);

  const availableCourtsForTime = useMemo(() => {
    if (!confirmSlot) return [] as CourtRow[];
    const selectedCourtIds = new Set(selectedSlots.map((s) => s.courtId));
    return courts.filter((c: CourtRow) => {
      if (selectedCourtIds.has(c.id)) return false;
      const courtBookings = bookings.filter(
        (b: BookingDayRow) => b.court_id === c.id && b.status !== "cancelled",
      );
      const endTime = addMinutes(confirmSlot.startTime, minDuration);
      return !courtBookings.some((b: BookingDayRow) => {
        const bStart = new Date(b.start_time);
        const bEnd = new Date(b.end_time);
        return confirmSlot.startTime < bEnd && endTime > bStart;
      });
    });
  }, [confirmSlot, selectedSlots, courts, bookings, minDuration]);

  return {
    selectedSlots,
    setSelectedSlots,
    confirmSlot,
    setConfirmSlot,
    handleSlotClick,
    handleAddCourt,
    handleRemoveCourt,
    maxAvailableMinutes,
    availableCourtsForTime,
  };
}
